// fastShots.ts

import * as React from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PbvRally, PbvShot } from "./RallyBoardPopup";

import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import type {
  KitchenArrivalRow,
  ShotNumberOverlay,
  Vec3,
} from "./RallyBoardPopup";
import { ShotNumberOverlayLayer } from "./RallyPopup";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { FirefightClip } from "./GameStory_firefights";
import {
  iconFastShot,
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import { ActiveRallyVideo } from "./ActiveRallyVideo";

export function VideoOverlayControls(props: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  rate: 0.25 | 0.5 | 1;
  setRate: (r: 0.25 | 0.5 | 1) => void;
  paused: boolean;
  setPaused: (p: boolean) => void;
}) {
  const { videoRef, rate, setRate, paused, setPaused } = props;

  const applyRate = React.useCallback(
    (r: 0.25 | 0.5 | 1) => {
      const v = videoRef.current;
      if (!v) return;
      v.defaultPlaybackRate = r;
      v.playbackRate = r;
    },
    [videoRef],
  );

  // keep rate applied even if browser resets it
  React.useEffect(() => {
    applyRate(rate);
  }, [rate, applyRate]);

  const togglePlay = React.useCallback(() => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      v.play().catch(() => {});
      setPaused(false);
    } else {
      v.pause();
      setPaused(true);
    }
  }, [videoRef, setPaused]);

  const step = React.useCallback(
    (deltaSeconds: number) => {
      const v = videoRef.current;
      if (!v) return;

      // pause while stepping for precision
      v.pause();
      setPaused(true);

      seekBySeconds(v, deltaSeconds);
    },
    [videoRef, setPaused],
  );

  const RateButton = (r: 0.25 | 0.5 | 1) => (
    <Button
      key={r}
      size="small"
      variant={rate === r ? "contained" : "outlined"}
      onClick={() => {
        setRate(r);
        applyRate(r);
      }}
      sx={{
        textTransform: "none",
        minHeight: 22,
        py: 0,
        px: 1,
        fontWeight: 900,
        fontSize: 12,
        borderRadius: 999,
        boxShadow: rate === r ? "0 0 0 1px rgba(255,255,255,0.35)" : "none",
        bgcolor: rate === r ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)",
        color: rate === r ? "rgba(0,0,0,0.92)" : "rgba(255,255,255,0.92)",
        borderColor: rate === r ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.28)",
        "&:hover": {
          bgcolor: rate === r ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.70)",
        },
      }}
    >
      {r}×
    </Button>
  );

  return (
    <Box
      sx={{
        position: "absolute",
        right: 10,
        bottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        pointerEvents: "auto",
        zIndex: 120,
      }}
    >
      {/* transport */}
      <Button
        size="small"
        onClick={() => step(-1)}
        sx={{
          minHeight: 22,
          py: 0,
          px: 1,
          fontWeight: 900,
          fontSize: 12,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,255,255,0.20)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.70)" },
        }}
        startIcon={<Replay10Icon sx={{ fontSize: 18 }} />}
      >
        -1s
      </Button>

      <Button
        size="small"
        onClick={togglePlay}
        sx={{
          minHeight: 22,
          py: 0,
          px: 1,
          fontWeight: 900,
          fontSize: 12,
          borderRadius: 999,
          bgcolor: paused ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)",
          color: paused ? "rgba(0,0,0,0.92)" : "rgba(255,255,255,0.92)",
          border: paused
            ? "1px solid rgba(0,0,0,0.18)"
            : "1px solid rgba(255,255,255,0.20)",
          "&:hover": {
            bgcolor: paused ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.70)",
          },
        }}
        startIcon={
          paused ? (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          ) : (
            <PauseIcon sx={{ fontSize: 18 }} />
          )
        }
      >
        {paused ? "Play" : "Pause"}
      </Button>

      <Button
        size="small"
        onClick={() => step(1)}
        sx={{
          minHeight: 22,
          py: 0,
          px: 1,
          fontWeight: 900,
          fontSize: 12,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,255,255,0.20)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.70)" },
        }}
        endIcon={<Forward10Icon sx={{ fontSize: 18 }} />}
      >
        +1s
      </Button>

      {/* rate */}
      <Box sx={{ display: "flex", gap: 0.6, ml: 0.5 }}>
        {RateButton(0.25)}
        {RateButton(0.5)}
        {RateButton(1)}
      </Box>
    </Box>
  );
}

export type FastShotClip = {
  key: string;
  event: FastShotEvent;
  clipStartMs: number;
  clipEndMs: number;
};

const MPH_PER_FT_PER_MS = 1000 / 1.4666666667; // ≈ 681.818...

export function computeMphFromOverlay(overlay: ShotNumberOverlay): {
  mph: number;
  distFt: number;
  dtMs: number;
  startMs: number;
  endMs: number;
} | null {
  // Prefer overlay’s already-extracted trajectoryStart/End if present
  const oStart = overlay.trajectoryStart;
  const oEnd = overlay.trajectoryEnd;

  const startMs =
    oStart?.ms ??
    overlay.rawShot?.resulting_ball_movement?.trajectory?.start?.ms;
  const endMs =
    oEnd?.ms ?? overlay.rawShot?.resulting_ball_movement?.trajectory?.end?.ms;

  const startLoc = (oStart?.location ??
    overlay.rawShot?.resulting_ball_movement?.trajectory?.start?.location) as
    | Vec3
    | null
    | undefined;
  const endLoc = (oEnd?.location ??
    overlay.rawShot?.resulting_ball_movement?.trajectory?.end?.location) as
    | Vec3
    | null
    | undefined;

  if (typeof startMs !== "number" || typeof endMs !== "number") return null;
  if (!startLoc || !endLoc) return null;

  const dtMs = endMs - startMs;
  if (!(dtMs > 0)) return null;

  const distFt = dist3(startLoc, endLoc);
  if (!(distFt > 0.01)) return null;

  const mph = (distFt / dtMs) * MPH_PER_FT_PER_MS;
  if (!Number.isFinite(mph)) return null;

  return { mph, distFt, dtMs, startMs, endMs };
}

export function buildFastShotClip(args: {
  event: FastShotEvent;
  preMs?: number;
  postMs?: number;
  rallyStartMs?: number;
  rallyEndMs?: number;
}): FastShotClip {
  const { event } = args;
  const preMs = args.preMs ?? 700;
  const postMs = args.postMs ?? 700;

  let clipStartMs = event.shotStartMs - preMs;
  let clipEndMs = event.shotEndMs + postMs;

  if (typeof args.rallyStartMs === "number")
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  if (typeof args.rallyEndMs === "number")
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);

  if (clipEndMs - clipStartMs < 400) clipEndMs = clipStartMs + 400;

  return {
    key: `r${event.rallyIndex}_s${event.shotIndex}_mph${Math.round(event.mph)}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event,
    clipStartMs,
    clipEndMs,
  };
}

export function buildRallySegmentUrl(args: {
  packageId: string;
  rallyIndex: number;
  startMs: number;
  endMs: number;
  download?: 0 | 1;
}): string {
  return (
    `/api/rally-segment-video` +
    `?packageId=${encodeURIComponent(args.packageId)}` +
    `&rallyIndex=${encodeURIComponent(String(args.rallyIndex))}` +
    `&startMs=${encodeURIComponent(String(args.startMs))}` +
    `&endMs=${encodeURIComponent(String(args.endMs))}` +
    `&download=${encodeURIComponent(String(args.download ?? 0))}`
  );
}

export type FastShotEvent = {
  rallyIndex: number;
  shotIndex: number; // overlay index i (0-based in overlays array)
  shotNumber: number; // 1-based within rally (overlay.shotNumber)
  team: 0 | 1;
  playerId: number;

  distFt: number;
  mph: number;
  thresholdMph: number;

  shotStartMs: number;
  shotEndMs: number;

  isVolley?: boolean;
  strokeType?: string;
  shotType?: string;

  // optional debug
  dtMs: number;
  distanceUnits: number; // feet or meters depending on your unit choice
};

export function computeFastShotsForRally(args: {
  rally: KitchenArrivalRow;
  rallyIndex: number;
  thresholdMph: number;
  getTeamFromPlayerId: (playerId: number) => 0 | 1;

  // guards
  minDtMs?: number; // ignore tiny dt
  maxDtMs?: number; // ignore huge dt
  requireCrossedNet?: boolean; // optional filter
}): FastShotEvent[] {
  const { rally, rallyIndex, thresholdMph, getTeamFromPlayerId } = args;
  const minDtMs = args.minDtMs ?? 40;
  const maxDtMs = args.maxDtMs ?? 2000;

  const shots = [
    ...(Array.isArray(rally?.shotNumberOverlays)
      ? rally.shotNumberOverlays
      : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: FastShotEvent[] = [];

  for (let i = 0; i < shots.length; i++) {
    const o = shots[i];
    const s = o.rawShot;

    const t0 = o.trajectoryStart?.ms ?? s?.start_ms ?? null;
    const t1 = o.trajectoryEnd?.ms ?? s?.end_ms ?? null;

    const p0 = o.trajectoryStart?.location ?? null;
    const p1 = o.trajectoryEnd?.location ?? null;

    if (t0 == null || t1 == null || !p0 || !p1) continue;

    const dtMs = t1 - t0;
    if (dtMs < minDtMs || dtMs > maxDtMs) continue;

    if (
      args.requireCrossedNet &&
      s?.resulting_ball_movement?.crossed_net === false
    )
      continue;

    const mphInfo = computeMphFromOverlay(o);
    if (!mphInfo) continue;

    const dUnits = dist3(p0, p1);
    const unitsPerSec = dUnits / (dtMs / 1000);

    // pick ONE conversion based on your unit decision:
    const mph = ftPerSecToMph(unitsPerSec);
    // const mph = mPerSecToMph(unitsPerSec);

    if (!Number.isFinite(mph)) continue;
    if (mph < thresholdMph) continue;

    const playerId = Number(o.playerId ?? s?.player_id);
    if (!Number.isFinite(playerId)) continue;

    out.push({
      rallyIndex,
      shotIndex: i,
      shotNumber: o.shotNumber,
      team: getTeamFromPlayerId(playerId),
      playerId,
      mph: o.rawShot?.resulting_ball_movement?.speed ?? 200, //mph, // prefer insights speed if present, else compute from trajectory
      thresholdMph,
      shotStartMs: s?.start_ms ?? t0,
      shotEndMs: s?.end_ms ?? t1,
      isVolley: !!s?.is_volley,
      strokeType: s?.stroke_type,
      shotType: s?.shot_type ?? o.shotType ?? undefined,
      dtMs,
      distanceUnits: dUnits,
      distFt: mphInfo.distFt,
    });
  }

  return out;
}

export function seekBySeconds(video: HTMLVideoElement, deltaSeconds: number) {
  const dur = Number.isFinite(video.duration) ? video.duration : null;
  const next = video.currentTime + deltaSeconds;
  if (dur == null) {
    video.currentTime = Math.max(0, next);
    return;
  }
  video.currentTime = Math.max(0, Math.min(dur, next));
}

function dist3(a: Vec3, b: Vec3) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// If your trajectory locations are FEET:
function ftPerSecToMph(ftps: number) {
  return ftps * 0.681818; // 1 ft/s = 0.681818 mph
}

// If your trajectory locations are METERS, use this instead:
// function mPerSecToMph(mps: number) { return mps * 2.23694; }

// fastReactions.ts
export type FastReactionEvent = {
  rallyIndex: number;
  shotIndex: number;

  team: 0 | 1;
  playerId: number;

  reactionMs: number;
  thresholdMs: number;

  // shot timing for seeking / display
  shotStartMs: number;
  shotEndMs: number;

  // optional UI bits
  isVolley?: boolean;
  strokeType?: string;
  shotType?: string;
};

// function shotWentOut(shot: any): boolean {
//   // Don’t count if they go out.
//   // Your data has resulting_ball_movement.trajectory.end.zone and we’ve seen "out".
//   return shot?.resulting_ball_movement?.trajectory?.end?.zone === "out";
// }

// function shotWentOut(overlay: ShotNumberOverlay): boolean {
//   return (
//     overlay?.rawShot?.resulting_ball_movement?.trajectory?.end?.zone === "out"
//   );
// }

function shotIsBad(overlay: ShotNumberOverlay): boolean {
  const s = overlay?.rawShot;

  // OUT via trajectory end zone
  if (s?.resulting_ball_movement?.trajectory?.end?.zone === "out") return true;

  const faults = s?.errors?.faults;

  // If PBV flags net faults explicitly
  if (faults?.net === true) return true;
  //if (faults?.tape === true) return true;

  // Some datasets store as strings/arrays
  const faultStr = JSON.stringify(faults ?? {});
  if (faultStr.includes("net") || faultStr.includes("tape")) return true;

  return false;
}

export type FastReactionClip = {
  key: string;
  event: FastReactionEvent;

  // clip window you request from /api/rally-segment-video
  clipStartMs: number;
  clipEndMs: number;
};

export function buildFastReactionClip(args: {
  event: FastReactionEvent;
  // these are global knobs you can tweak later
  preMs?: number;
  postMs?: number;
  // optional clamp if you know rally bounds
  rallyStartMs?: number;
  rallyEndMs?: number;
}): FastReactionClip {
  const { event } = args;
  const preMs = args.preMs ?? 1200;
  const postMs = args.postMs ?? 900;

  let clipStartMs = event.shotStartMs - preMs;
  let clipEndMs = event.shotEndMs + postMs;

  if (typeof args.rallyStartMs === "number")
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  if (typeof args.rallyEndMs === "number")
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);

  // hard minimum size just to avoid tiny segments if something is weird
  if (clipEndMs - clipStartMs < 400) clipEndMs = clipStartMs + 400;

  return {
    key: `r${event.rallyIndex}_s${event.shotIndex}_t${event.thresholdMs}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event,
    clipStartMs,
    clipEndMs,
  };
}

// FastReactionsPopup.tsx

type Mode = "thisRally" | "allRallies";

export function FastShotsPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;

  packageId: string;

  // current selection context
  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];

  // derived data that you compute outside (recommended)
  clipsThisRally: FastShotClip[];
  clipsAll: FastShotClip[];

  // optional helpers for display
  playerLabel?: (playerId: number) => string;
  renderPlayerIdentity: RenderPlayerIdentity;
  teamLabel?: (team: 0 | 1) => string;

  teamColor: (team: 0 | 1, alpha?: number) => string;
  teamName?: (team: 0 | 1) => string; // e.g. "Us" / "Them" or "Team 0" / "Team 1"
  firefightsClips: FirefightClip[]; // <-- NEW, for overlay in video
  firefightRallySet: Set<number>; // <-- NEW, for conditional overlay in timeline
}) {
  const {
    open,
    anchorEl,
    onClose,
    packageId,
    activeRallyIndex,
    rows,
    clipsThisRally,
    clipsAll,
    playerLabel,
    teamLabel,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
  } = props;

  //   const [mode, setMode] = React.useState<Mode>("thisRally");
  const clips = clipsAll; //mode === "thisRally" ? clipsThisRally : clipsAll;

  // keep a selected clip
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    // When opening or switching modes, default select first clip
    if (!open) return;
    const first = clips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, activeRallyIndex]); // activeRallyIndex changes should reset selection

  const selected = clips.find((c) => c.key === selectedKey) ?? clips[0] ?? null;

  // Build URL for the selected clip
  const videoUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.event.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0, //1,
    });
  }, [packageId, selected?.key]);

  // const videoRef = React.useRef<HTMLVideoElement | null>(null);
  // const [playbackRate, setPlaybackRate] = React.useState<0.25 | 0.5 | 1>(1);

  const clipVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const rallyVideoRef = React.useRef<HTMLVideoElement | null>(null);

  const [clipRate, setClipRate] = React.useState<0.25 | 0.5 | 1>(1);
  const [rallyRate, setRallyRate] = React.useState<0.25 | 0.5 | 1>(1);

  const clipShouldMute = clipRate < 1;
  const rallyShouldMute = rallyRate < 1;

  const [clipPaused, setClipPaused] = React.useState(false);
  const [rallyPaused, setRallyPaused] = React.useState(false);

  //   React.useEffect(() => {
  //     const v = videoRef.current;
  //     if (!v) return;

  //     // Force autoplay on src change
  //     const tryPlay = async () => {
  //       try {
  //         // ensure rate is applied before play
  //         v.playbackRate = playbackRate;
  //         await v.play();
  //       } catch {
  //         // autoplay can be blocked if not muted; user can click video once
  //       }
  //     };
  //     void tryPlay();
  //   }, [videoUrl]);

  //   const applyRateAndPlay = React.useCallback(
  //     (rateOverride?: 0.25 | 0.5 | 1) => {

  //       const v = videoRef.current;
  //       if (!v) return;

  //               console.log("rate now", v.playbackRate);

  //       const rate = rateOverride ?? desiredRateRef.current;
  //       v.playbackRate = rate;

  //       // ensure playing (don’t spam; one call is fine)
  //       v.play().catch(() => {});
  //               console.log("2 rate now", v.playbackRate);

  //     },
  //     [],
  //   );

  // const applyRate = React.useCallback((rate: 0.25 | 0.5 | 1) => {
  //   const v = videoRef.current;
  //   if (!v) return;

  //   // Chrome often respects defaultPlaybackRate more reliably across reload/loop.
  //   v.defaultPlaybackRate = rate;
  //   v.playbackRate = rate;

  //   console.log("applied rate", rate, "actual", v.playbackRate);
  // }, []);

  // React.useLayoutEffect(() => {
  //   if (!videoUrl) return;
  //   applyRate(playbackRate);
  // }, [videoUrl, playbackRate, applyRate]);

  //   React.useEffect(() => {
  //     // do it now...
  //     applyRateAndPlay();

  //     // ...and again on next frames (ref/video sometimes not ready yet)
  //     const id1 = requestAnimationFrame(() => applyRateAndPlay());
  //     // const id2 = requestAnimationFrame(() => applyRateAndPlay());

  //     return () => {
  //       cancelAnimationFrame(id1);
  //       //   cancelAnimationFrame(id2);
  //     };
  //   }, [videoUrl, applyRateAndPlay]);

  const [pos, setPos] = React.useState<{ x: number; y: number }>({
    x: 18,
    y: 50,
  });

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      const nextX = Math.max(
        0,
        Math.min(window.innerWidth - 220, d.startPosX + dx),
      );
      const nextY = Math.max(
        0,
        Math.min(window.innerHeight - 140, d.startPosY + dy),
      );
      setPos({ x: nextX, y: nextY });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  const desiredRateRef = React.useRef<0.25 | 0.5 | 1>(1);

  // React.useEffect(() => {
  //   desiredRateRef.current = playbackRate;
  //   // Apply instantly (no play spam)
  //   const v = videoRef.current;
  //   if (v) v.playbackRate = playbackRate;
  // }, [playbackRate]);
  //   type SortDir = "asc" | "desc";
  //   const [sortDir, setSortDir] = React.useState<SortDir>("asc");

  type SortKey = "mph" | "rally";
  type SortDir = "asc" | "desc";

  const [sortKey, setSortKey] = React.useState<SortKey>("mph");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const [playerFilter, setPlayerFilter] = React.useState<number | null>(null);

  const filteredClips = React.useMemo(() => {
    if (playerFilter == null) return clips;
    return clips.filter((c) => c.event.playerId === playerFilter);
  }, [clips, playerFilter]);

  //const filteredClips = clips; // (we’ll insert player filtering below)
  const sortedClips = React.useMemo(() => {
    const arr = [...filteredClips];
    arr.sort((a, b) => {
      const av = sortKey === "mph" ? a.event.mph : a.event.rallyIndex;
      const bv = sortKey === "mph" ? b.event.mph : b.event.rallyIndex;
      const d = av - bv;
      return sortDir === "asc" ? d : -d;
    });
    return arr;
  }, [filteredClips, sortKey, sortDir]);

  const playerIds = React.useMemo(() => {
    const s = new Set<number>();
    for (const c of clips) s.add(c.event.playerId);
    return Array.from(s).sort((a, b) => a - b);
  }, [clips]);

  const selectedRow = React.useMemo(() => {
    if (!selected) return null;
    return props.rows?.[selected.event.rallyIndex] ?? null;
  }, [props.rows, selected?.key]);

  const clipRow = React.useMemo(() => {
    if (!selectedRow || !selected) return null;
    return selectedRow;
  }, [selectedRow, selected?.key]);

  const [seedNonce, setSeedNonce] = React.useState(0);

  React.useEffect(() => {
    // bump nonce anytime the selected clip changes
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [
    selected?.event?.rallyIndex,
    selected?.event?.shotIndex,
    Math.round(selected?.clipStartMs ?? 0),
    Math.round(selected?.clipEndMs ?? 0),
  ]);

  const selectedLoopRegion = React.useMemo(() => {
    if (!selectedRow || !selected) return undefined;
    const segmentStartMs = selectedRow.segmentStartMs ?? selectedRow.startMs ?? 0;
    return {
      enabled: true,
      aSec: Math.max(0, (selected.clipStartMs - segmentStartMs) / 1000),
      bSec: Math.max(0.5, (selected.clipEndMs - segmentStartMs) / 1000),
      nonce: seedNonce,
    };
  }, [selectedRow, selected?.key, seedNonce]);

  const rallyVideoUrl = selectedRow?.videoUrl ?? null;

  // console.log("selectedRow", selectedRow);
  // console.log("rallyVideoUrl", rallyVideoUrl);

  //   React.useEffect(() => {
  //     if (!open) return;
  //     const first = sortedClips[0]?.key ?? null;
  //     setSelectedKey(first);
  //   }, [open, playerFilter, sortDir]); // keep it simple

  React.useEffect(() => {
    if (!open) return;
    const first = sortedClips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, playerFilter, sortKey, sortDir]);

  // const shouldMute = playbackRate < 1;

  // React.useEffect(() => {
  //   const v = videoRef.current;
  //   if (!v) return;

  //   v.muted = shouldMute;
  //   v.defaultMuted = shouldMute;

  //   // If it's playing, nudge it to keep going (Safari/Chrome can hiccup on mute toggles)
  //   if (!v.paused) v.play().catch(() => {});
  // }, [shouldMute, videoUrl]);

  React.useEffect(() => {
    const v = clipVideoRef.current;
    if (!v) return;
    v.muted = clipShouldMute;
    v.defaultMuted = clipShouldMute;
  }, [clipShouldMute, videoUrl]);

  React.useEffect(() => {
    const v = rallyVideoRef.current;
    if (!v) return;
    v.muted = rallyShouldMute;
    v.defaultMuted = rallyShouldMute;
  }, [rallyShouldMute, rallyVideoUrl]);

  React.useEffect(() => {
    setClipPaused(false);
  }, [videoUrl]);

  React.useEffect(() => {
    setRallyPaused(false);
  }, [rallyVideoUrl]);

  if (!open) return null;

  // NOTE: I'm not using MUI Popover here because you have your own floating panel patterns.
  // If you want this anchored like your other popups, swap outer container with Popover.
  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1320,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",

        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        // border: "3px solid yellow !important",
        border: "1px solid rgba(0,0,0,0.14)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontWeight: 900, mr: 1 }}>
            {iconFastShot(16)} Fast Shots
          </Typography>

          <Typography sx={{ opacity: 0.75, fontSize: 12 }}>
            {filteredClips.length} clips
          </Typography>

          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
            <Button
              size="small"
              variant={playerFilter == null ? "contained" : "outlined"}
              onClick={() => setPlayerFilter(null)}
              sx={{
                textTransform: "none",
                minHeight: 22,
                py: 0,
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              All
            </Button>

            {playerIds.map((pid) => (
              <Button
                key={pid}
                size="small"
                variant={playerFilter === pid ? "contained" : "outlined"}
                onClick={() => setPlayerFilter(pid)}
                sx={{
                  textTransform: "none",
                  minHeight: 22,
                  py: 0,
                  fontWeight: 900,
                  fontSize: 12,
                }}
              >
                {playerLabel ? playerLabel(pid) : `P${pid}`}
              </Button>
            ))}
          </Box> */}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setPlayerFilter(null)}
              sx={{
                textTransform: "none",
                minHeight: 22,
                py: 0,
                px: 1,
                minWidth: 0,
                fontWeight: 900,
                fontSize: 12,
                border: "1px solid transparent",
                bgcolor: playerFilter == null ? "rgba(0,0,0,0.07)" : "white",
                boxShadow:
                  playerFilter == null
                    ? "inset 0 0 0 1px rgba(0,0,0,0.20)"
                    : "none",
                "&:hover": {
                  borderColor: "rgba(0,0,0,0.18)",
                  bgcolor:
                    playerFilter == null
                      ? "rgba(0,0,0,0.10)"
                      : "rgba(0,0,0,0.03)",
                },
              }}
            >
              All
            </Button>

            {playerIds.map((pid) => {
              const isSelected = playerFilter === pid;
              const team = pid < 2 ? 0 : 1;

              return (
                <Button
                  key={pid}
                  size="small"
                  variant="text"
                  onClick={() => setPlayerFilter(pid)}
                  sx={{
                    textTransform: "none",
                    minHeight: 22,
                    py: 0,
                    px: 0.5,
                    minWidth: 0,
                    fontWeight: 900,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    border: "1px solid transparent",
                    bgcolor: isSelected
                      ? props.teamColor
                        ? props.teamColor(team, 0.18)
                        : "rgba(0,0,0,0.08)"
                      : "white",
                    boxShadow: isSelected
                      ? props.teamColor
                        ? `inset 0 0 0 1px ${props.teamColor(team, 0.55)}`
                        : "inset 0 0 0 1px rgba(0,0,0,0.22)"
                      : "none",
                    "&:hover": {
                      borderColor: props.teamColor
                        ? props.teamColor(team, 0.45)
                        : "rgba(0,0,0,0.18)",
                      bgcolor: isSelected
                        ? props.teamColor
                          ? props.teamColor(team, 0.24)
                          : "rgba(0,0,0,0.10)"
                        : "rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  {renderPlayerIdentity
                    ? renderPlayerIdentity({
                        playerId: pid,
                        labelOnly: false,
                        size: 40,
                        borderRadius: 5,
                        fontSize: 12,
                      })
                    : playerLabel
                      ? playerLabel(pid)
                      : `p${pid}`}
                </Button>
              );
            })}
          </Box>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Close
        </Button>
      </Box>

      {/* Body */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left: list/table */}
        <Box
          sx={{
            width: 420,
            maxWidth: "50%",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSort("rally")}
                >
                  Rally{" "}
                  {sortKey === "rally" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Player</TableCell>
                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  align="right"
                  //   onClick={() =>
                  //     setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  //   }
                  onClick={() => toggleSort("mph")}
                >
                  Fast Shot {sortDir === "asc" ? "▲" : "▼"}
                </TableCell>{" "}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedClips.map((c) => {
                const e = c.event;
                const isSel = c.key === selectedKey;
                return (
                  <TableRow
                    key={c.key}
                    hover
                    onClick={() => setSelectedKey(c.key)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSel ? "rgba(0,0,0,0.06)" : undefined,
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {e.rallyIndex + 1}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        {/* <Box sx={{ fontWeight: 800, fontSize: 13 }}>
                          {playerLabel
                            ? playerLabel(e.playerId)
                            : `P${e.playerId}`}
                        </Box>
 */}

                        <Box sx={{ minWidth: 0 }}>
                          {renderPlayerIdentity ? (
                            renderPlayerIdentity({
                              playerId: e.playerId,
                              labelOnly: false,
                              size: 26,
                              borderRadius: 5,
                              fontSize: 12,
                            })
                          ) : (
                            <Box sx={{ fontWeight: 800, fontSize: 13 }}>
                              {playerLabel
                                ? playerLabel(e.playerId)
                                : `p${e.playerId}`}
                            </Box>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              px: 0.8,
                              py: 0.15,
                              borderRadius: 999,
                              fontWeight: 900,
                              fontSize: 11,
                              lineHeight: 1.2,
                              // bgcolor: props.teamColor
                              //   ? props.teamColor(e.team, 0.18)
                              //   : "rgba(0,0,0,0.08)",
                              bgcolor: props.teamColor
                                ? props.teamColor(e.team, 0.12)
                                : "rgba(0,0,0,0.08)",
                              color: props.teamColor
                                ? props.teamColor(e.team, 1)
                                : "inherit",
                              border: props.teamColor
                                ? `1px solid ${props.teamColor(e.team, 0.35)}`
                                : "1px solid rgba(0,0,0,0.10)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {props.teamName
                              ? props.teamName(e.team)
                              : `Team ${e.team}`}
                          </Box>

                          <Box
                            sx={{
                              fontSize: 12,
                              opacity: 0.75,
                              whiteSpace: "nowrap",
                            }}
                          >
                            • shot {e.shotIndex + 1}
                            {e.isVolley ? " • volley" : ""}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {Math.round(e.mph)} mph
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Right: video + overlay “enclosing stuff” */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            {selected ? (
              <Typography sx={{ fontWeight: 900 }}>
                Rally {selected.event.rallyIndex + 1} • Shot{" "}
                {selected.event.shotIndex + 1} •{" "}
                {Math.round(selected.event.mph)} mph (approx)
              </Typography>
            ) : (
              <Typography sx={{ opacity: 0.75 }}>
                No fast shots for this selection.
              </Typography>
            )}
          </Box>

          <Box>
            <CondensedRallyTimeline_ContextOnly
              activeRally={selectedRow}
              rows={rows}
              barsAreaHeightPx={40}
              showLegend={false}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "black",
            }}
          >

            {videoUrl && selected && clipRow ? (
              <Box
                className="video-clip-container"
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <ActiveRallyVideo
                  insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                  renderPlayerIdentity={renderPlayerIdentity}
                  aiContextLabel="Fast Shots"
                  focusedFilter={null}
                  packageId={packageId}
                  row={clipRow}
                  rows={rows}
                  activeShotNumber={selected.event.shotNumber}
                  showCondensedTimeline={false}
                  fontColor={"white"}
                  controlsPlacement={"left"}
                  firefightsClips={firefightsClips}
                  firefightRallySet={firefightRallySet}
                  showRateControls={true}
                  showTitle={false}
                  showScoreEtc={false}
                  showAi={true}
                  aiDrawerMode={"split"}
                  showLoopScrubber={true}
                  defaultLoopUiOpen={false}
                  showPopoutButton={true}
                  isInFullPopup={false}
                  seedPlayback={{
                    timeSec: selectedLoopRegion?.aSec ?? 0,
                    paused: false,
                    nonce: seedNonce, // or any changing value when switching clips
                  }}
                  seedLoopRegion={selectedLoopRegion}
                  seekToActiveShotOnChange={false}
                />

                {/* keep your existing label overlay (optional) */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    px: 1,
                    py: 0.6,
                    borderRadius: 1.5,
                    bgcolor: "rgba(0,0,0,0.65)",
                    color: "white",
                    fontSize: 12,
                    lineHeight: 1.25,
                    pointerEvents: "none",
                  }}
                >
                  <Box sx={{ fontWeight: 900, fontSize: 13 }}>
                    {"Shot " +
                      (selected.event.shotNumber ?? "") +
                      " • " +
                      (selected.event.mph?.toFixed(0) ?? "") +
                      "mph"}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2, color: "rgba(255,255,255,0.75)" }}>
                No clip selected.
              </Box>
            )}
          </Box>


        </Box>
      </Box>
    </Paper>
  );
}
